import { VodApi } from '../../../api/VodApi';
import { AssetType } from '../../../api/models/Shared.types';
import { VodDetailDescriptionDataSource } from './VodDetailDescriptionDataSource';
import { PriceApi } from '../../../api/price';
import { Log } from '@lightningjs/sdk';
import { VodStreamRights } from './VodStreamRights';
import { UserUtil } from '../../utility/UserUtil';
import { VodAsset } from './VodAsset';
import { SharedFavouriteActionButton } from '../../../pages/detail/actionRowButtons/SharedFavouriteActionButton';
import { DetailTranslationKeys, ErrorTranslationKeys, SettingsKeys } from '../../../api/models/TranslationKeys';
import { TabbedEpisodeRibbonDataSource } from '../../cms/TabbedEpisodeRibbonDataSource';
import { DetailDataSource } from '../DetailDataSource';
import { VodWatchAction } from '../actions/VodWatchAction';
import { ActionButton } from '../../../pages/detail/actionRowButtons/ActionButton';
import { VodBundleAction } from '../actions/VodBundleAction';
import { RentAction } from '../actions/RentAction';
import { ShowDetailAction } from '../actions/ShowDetailAction';
import { atvPromiseAll } from '../../utility/PromiseUtil';
import { WatchTrailerAction } from '../actions/WatchTrailerAction';
import { BookmarkUtility } from '../../utility/BookmarkUtility';
import { StaticPageUrls } from '../../../api/analytics/ClientEvents';
import { Backend } from '../../../api/Backend';
import { IActionButtonDefinition, IDetailModel, NextEpisodeInfo } from '../detail.types';
import { IAssetSummary, IValidQuotePriceResponse3, IVodPriceResponse3 } from '../../../api/models/AndromeTV.types';
import { RibbonDataSource } from '../../cms/RibbonDataSource';

export class VodDetailDataSource extends DetailDataSource {
  constructor(backend: Backend) {
    super(backend);
  }

  override logPageView() {
    this.backend.logService.trackPageView(StaticPageUrls.vodDetail);
  }

  override load(id: string): Promise<IDetailModel> {
    return VodApi.asset(this.backend, id)
      .then(result => {
        const vod = new VodAsset(result);

        const quotePricing = this._fetchQuoteAndPricing(this.backend, vod.id);
        const nextEpisodeProm = this._checkNextEpi(this.backend, vod);

        return atvPromiseAll([quotePricing, nextEpisodeProm]).then(([vodStreamRights, nextEpisodeInfo]) => {
          const descrLogic = new VodDetailDescriptionDataSource(vod, AssetType.VOD, this.backend, vodStreamRights);
          const streamRights = vodStreamRights;
          const images = {
            detailPoster: vod.wallpaperImage ? vod.wallpaperImage : vod.posterImage,
          };
          const res: IDetailModel = {
            description: descrLogic,
            images: images,
            buttons: this.getButtons(this.backend, vod, streamRights, nextEpisodeInfo),
            ribbonDatasources: this.getRibbons(this.backend, vod),
          };
          return res;
        });
      })
      .catch(err => {
        Log.error('[VodDetailInitializer] error', err);
        throw this.backend.translations.forKey(ErrorTranslationKeys.error_general);
      });
  }

  _fetchQuoteAndPricing(backend: Backend, vodId: string): Promise<VodStreamRights> {
    const requests = [
      backend.cachedQuoteApi.list(backend),
      PriceApi.forVod(backend, vodId, {
        entitlementId: backend.sessionInfo?.device.entitlementId,
        catalogId: backend.deviceProfile?.profile.catalog,
      }),
    ];

    return Promise.all(requests).then(([quotes, price]) => {
      return new VodStreamRights(vodId, quotes as IValidQuotePriceResponse3[], price as IVodPriceResponse3);
    });
  }

  /**

   * @param {Backend} backend
   * @param {VodAsset} vod
   * @param {VodStreamRights} streamRights
   * @param nextEpisodeInfo
   * @return {IActionButtonDefinition[]}
   */
  getButtons(
    backend: Backend,
    vod: VodAsset,
    streamRights: VodStreamRights,
    nextEpisodeInfo: NextEpisodeInfo
  ): IActionButtonDefinition[] {
    const result: IActionButtonDefinition[] = [];

    const showWatch = this.showVodWatchButton(backend, vod, streamRights, nextEpisodeInfo);
    const showRent = this.showRentButton(backend, vod, streamRights);
    if (backend.isAuthorized()) {
      if (showWatch) result.push(this.getWatchButton(backend, vod, streamRights, nextEpisodeInfo));

      if (!showWatch && showRent) result.push(this.getRentButton(backend, vod, streamRights));

      // if it is a series (has nextepisodeinfo) do not add bundle here, do later
      if (!nextEpisodeInfo && this.showBundleButton(backend, vod, streamRights))
        result.push(this.getBundleButton(backend, vod, streamRights));
    } else {
      if (showWatch) result.push(this.getWatchButton(backend, vod, streamRights, nextEpisodeInfo));
    }

    if (this.showTrailerButton(backend, vod, streamRights))
      result.push(this.getTrailerButton(backend, vod, streamRights));

    if (backend.isAuthorized()) {
      if (showWatch && showRent) result.push(this.getRentButton(backend, vod, streamRights));
      //add favourite button
      result.push(this.getFavouriteButton(backend, vod));
      //if it is a series (has nextepisodeinfo) add bundle here
      if (nextEpisodeInfo && this.showBundleButton(backend, vod, streamRights))
        result.push(this.getBundleButton(backend, vod, streamRights));
    }

    const seriesButton = this.getSeriesButton(backend, vod);
    if (this.showSeriesButton(backend, vod) && seriesButton) result.push(seriesButton);

    return result;
  }

  showRentButton(backend: Backend, vod: VodAsset, streamRights: VodStreamRights) {
    return UserUtil.rentingAllowed(backend) && !vod.isExpired() && streamRights.canRent();
  }

  /* eslint-disable no-unused-vars */
  getRentButton(backend: Backend, vod: VodAsset, streamRights: VodStreamRights): IActionButtonDefinition {
    const actionHandler = new RentAction(backend, AssetType.VOD, vod, streamRights);
    return {
      type: ActionButton,
      actionHandler,
      label: backend.translations.forKey(DetailTranslationKeys.detail_btn_rent_now),
    };
  }

  showVodWatchButton(backend: Backend, vod: VodAsset, streamRights: VodStreamRights, nextEpisodeInfo: NextEpisodeInfo) {
    const showSeriesWatchButton = backend.globalSettings.boolValue(SettingsKeys.showSeriesWatchButton);
    return (
      (streamRights && streamRights.canWatch() && !vod.isExpired()) ||
      // if vod is expired but you still have valid quote
      (streamRights && streamRights.hasWatchableOttTVOD() && vod.isExpired()) ||
      (!backend.isAuthorized() && !vod.isExpired()) ||
      ((showSeriesWatchButton ? showSeriesWatchButton : false) &&
        nextEpisodeInfo &&
        nextEpisodeInfo.streamRights &&
        ((nextEpisodeInfo.streamRights.canWatch() && !nextEpisodeInfo.asset.isExpired()) ||
          (nextEpisodeInfo.streamRights.hasWatchableOttTVOD() && nextEpisodeInfo.asset.isExpired())))
    );
  }

  getWatchButton(
    backend: Backend,
    vod: VodAsset,
    streamRights: VodStreamRights,
    nextEpisodeInfo: NextEpisodeInfo
  ): IActionButtonDefinition {
    const selectedVod = nextEpisodeInfo ? nextEpisodeInfo.asset : vod;
    const selectedRights = nextEpisodeInfo ? nextEpisodeInfo.streamRights : streamRights;
    const actionHandler = new VodWatchAction(backend, selectedVod, selectedRights);
    return {
      type: ActionButton,
      actionHandler,
      label: backend.translations.forKey(DetailTranslationKeys.detail_btn_watch_now),
    };
  }

  showTrailerButton(backend: Backend, vod: VodAsset, streamRights: VodStreamRights) {
    return streamRights.hasTrailer() && !vod.isExpired();
  }

  getTrailerButton(backend: Backend, vod: VodAsset, streamRights: VodStreamRights): IActionButtonDefinition {
    const actionHandler = new WatchTrailerAction(backend, streamRights.trailerStreams, vod);
    return {
      type: ActionButton,
      actionHandler,
      label: backend.translations.forKey(DetailTranslationKeys.detail_btn_trailer),
    };
  }

  showBundleButton(backend: Backend, vod: VodAsset, streamRights: VodStreamRights) {
    return streamRights.hasBundles() && !vod.isExpired();
  }

  getBundleButton(backend: Backend, vod: VodAsset, streamRights: VodStreamRights): IActionButtonDefinition {
    const actionHandler = new VodBundleAction(backend, '', streamRights);
    return {
      type: ActionButton,
      actionHandler,
      label: backend.translations.forKey(DetailTranslationKeys.detail_btn_bundle),
    };
  }

  showSeriesButton(backend: Backend, vod: VodAsset) {
    return vod.seriesId;
  }

  getSeriesButton(backend: Backend, vod: VodAsset): IActionButtonDefinition | undefined {
    if (!vod.seriesId) return;
    const actionHandler = new ShowDetailAction(backend, vod.seriesId, AssetType.VOD);
    return {
      type: ActionButton,
      actionHandler,
      label: backend.translations.forKey(DetailTranslationKeys.detail_btn_series),
    };
  }

  getFavouriteButton(backend: Backend, vod: VodAsset): IActionButtonDefinition {
    return {
      type: SharedFavouriteActionButton,
      assetId: vod.id,
      assetType: AssetType.VOD,
      assetIsAdult: vod.adult,
    };
  }
  getRibbons(backend: Backend, asset: VodAsset) {
    let ribbonDataSources: RibbonDataSource[] = [];

    const episodeDs = this.createEpisodesDataSource(backend, asset);
    if (episodeDs) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ribbonDataSources.push(episodeDs);
    }
    ribbonDataSources = ribbonDataSources.concat(this.createRelatedDataSources(backend, asset.type, asset.id, false));

    return ribbonDataSources;
  }

  /**
   * @param {Backend} backend
   * @param {VodAsset} asset
   */
  createEpisodesDataSource(backend: Backend, asset: VodAsset): TabbedEpisodeRibbonDataSource | undefined {
    if (asset.type !== AssetType.SERIES && !asset.seriesId) {
      return undefined;
    }

    return new TabbedEpisodeRibbonDataSource(
      backend,
      asset,
      backend.translations.forKey(DetailTranslationKeys.detail_btn_all_episodes)
    );
  }

  _checkNextEpi(backend: Backend, vod: VodAsset) {
    const showSeriesButton = backend.globalSettings.boolValue(SettingsKeys.showSeriesWatchButton);
    if (!(showSeriesButton ? showSeriesButton : false)) return Promise.resolve(undefined);

    if (vod.type === AssetType.SERIES && backend.isAuthorized()) {
      return backend.cachedBookmarkApi
        .seriesBookmarks(backend, vod.id, AssetType.VOD)
        .then(bookmarks => {
          const latest = BookmarkUtility.getLatestBookmark(bookmarks);

          if (bookmarks && bookmarks.length === 0)
            return vod.episodes && vod.episodes.length > 0 ? vod.episodes[0] : undefined;

          let nextEpi = undefined;
          if (latest && BookmarkUtility.isFullyWatched(backend, latest)) {
            nextEpi = this._getEpiAfter(vod, latest.id);
          } else if (latest) {
            nextEpi = vod.episodes?.find((epi: IAssetSummary) => epi.id === latest.id);
          }
          return nextEpi;
        })
        .then(selectedEpisode => {
          if (selectedEpisode) {
            return this._getEpisode(backend, selectedEpisode);
          }
          return undefined;
        });
    }
  }

  /**
   * falls back to first epi of vod if none after epiId
   * @private
   */
  _getEpiAfter(vod: VodAsset, epiId: string) {
    if (vod && vod.episodes && vod.episodes.some((ep: IAssetSummary) => ep.id === epiId)) {
      const epsIndex = vod.episodes.findIndex((ep: IAssetSummary) => ep.id === epiId);

      if (epsIndex >= 0 && epsIndex + 1 < vod.episodes.length) {
        return vod.episodes[epsIndex + 1];
      } else if (epsIndex && epsIndex + 1 >= vod.episodes.length) {
        return vod.episodes.length > 0 ? vod.episodes[0] : undefined;
      }
    }

    return undefined;
  }

  _getEpisode(backend: Backend, selectedEpisode: IAssetSummary) {
    return VodApi.asset(backend, selectedEpisode.id).then(result => {
      if (result) {
        return this._fetchQuoteAndPricing(backend, result.id).then(r => {
          return {
            asset: new VodAsset(result),
            streamRights: r,
          };
        });
      }
      return undefined;
    });
  }
}
